import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import NotificationBell from '../components/NotificationBell';
import { config } from '../utils/config';

const Help = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [activeSection, setActiveSection] = useState('faq');
  const [openFaq, setOpenFaq] = useState(null);
  const [contact, setContact] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContact((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const labels = {
      order: 'Commande',
      payment: 'Paiement',
      delivery: 'Livraison',
      account: 'Compte',
      other: 'Autre',
    };
    const title = labels[contact.subject] || contact.subject || 'Contact';
    const subjectLine = `[Maxfly] ${title}`;
    const body = `Nom : ${contact.name}\nEmail : ${contact.email}\n\n${contact.message}`;
    window.location.href = `mailto:${config.CONTACT_EMAIL}?subject=${encodeURIComponent(subjectLine)}&body=${encodeURIComponent(body)}`;
    toast.success('Votre messagerie s’ouvre : envoyez le message pour nous joindre.');
  };

  const faqCategories = {
    general: {
      title: 'Questions générales',
      icon: '❓',
      questions: [
        {
          q: 'Qu\'est-ce que Maxfly ?',
          a: 'Maxfly est une plateforme e-commerce qui permet aux clients en RDC de commander facilement des produits sur des plateformes internationales comme Shein, Amazon, Alibaba, etc. Nous gérons tout le processus de commande, paiement et livraison pour vous.',
        },
        {
          q: 'Comment fonctionne Maxfly ?',
          a: '1. Créez un compte (Inscription) ou connectez-vous\n2. Trouvez un produit sur une plateforme internationale (Shein, Amazon, etc.)\n3. Créez une commande en partageant le lien ou une capture d\'écran\n4. Un agent définit le prix (produit + commission + livraison)\n5. Payez via Stripe ou Mobile Money\n6. Suivez votre commande jusqu\'à la livraison',
        },
        {
          q: 'Quelles plateformes sont supportées ?',
          a: 'Nous supportons actuellement Shein, Amazon, Alibaba, AliExpress et d\'autres plateformes internationales. Vous pouvez commander depuis n\'importe quelle plateforme en partageant le lien du produit.',
        },
        {
          q: 'Combien coûte le service ?',
          a: 'Maxfly applique une commission de 15% sur le prix du produit, plus les frais de livraison calculés selon le poids et la destination. Le prix total vous est communiqué avant le paiement.',
        },
      ],
    },
    orders: {
      title: 'Commandes',
      icon: '📦',
      questions: [
        {
          q: 'Comment créer une commande ?',
          a: 'Allez dans "Nouvelle Commande", partagez le lien du produit ou téléchargez une capture d\'écran, sélectionnez la plateforme, ajoutez les détails (taille, couleur, quantité) et soumettez. Un agent validera et définira le prix.',
        },
        {
          q: 'Combien de temps prend la validation d\'une commande ?',
          a: 'Un agent examine généralement votre commande sous 24-48 heures. Vous recevrez une notification dès que le prix est défini.',
        },
        {
          q: 'Puis-je annuler une commande ?',
          a: 'Vous pouvez annuler une commande tant qu\'elle n\'a pas été payée. Une fois payée, contactez le support pour toute demande d\'annulation.',
        },
        {
          q: 'Comment modifier une commande ?',
          a: 'Une fois créée, une commande ne peut pas être modifiée directement. Contactez le support si vous devez apporter des changements.',
        },
      ],
    },
    payment: {
      title: 'Paiement',
      icon: '💳',
      questions: [
        {
          q: 'Quels modes de paiement sont acceptés ?',
          a: 'Nous acceptons les paiements par carte bancaire (Stripe) et Mobile Money (Orange Money). D\'autres méthodes seront ajoutées prochainement.',
        },
        {
          q: 'Le paiement est-il sécurisé ?',
          a: 'Oui, tous les paiements sont sécurisés via Stripe, un leader mondial du paiement en ligne. Vos informations bancaires ne sont jamais stockées sur nos serveurs.',
        },
        {
          q: 'Que se passe-t-il si mon paiement échoue ?',
          a: 'Si votre paiement échoue, votre commande reste en attente. Vous pouvez réessayer le paiement depuis votre dashboard. Contactez le support si le problème persiste.',
        },
        {
          q: 'Puis-je payer en plusieurs fois ?',
          a: 'Actuellement, nous n\'offrons pas de paiement en plusieurs fois. Le paiement doit être effectué en une seule fois avant l\'expédition.',
        },
      ],
    },
    delivery: {
      title: 'Livraison',
      icon: '🚚',
      questions: [
        {
          q: 'Combien de temps prend la livraison ?',
          a: 'La livraison prend généralement 10-15 jours ouvrables après l\'expédition, selon la destination et le transporteur.',
        },
        {
          q: 'Comment suivre ma commande ?',
          a: 'Une fois votre commande expédiée, vous recevrez un Tracking ID. Vous pouvez suivre votre colis depuis votre dashboard ou la page de suivi détaillée.',
        },
        {
          q: 'Où puis-je me faire livrer ?',
          a: 'Nous livrons partout en RDC. Vous pouvez définir votre adresse de livraison dans votre profil. Assurez-vous que l\'adresse est complète et précise.',
        },
        {
          q: 'Que faire si mon colis est endommagé ?',
          a: 'Contactez immédiatement le support avec des photos du colis endommagé. Nous examinerons votre cas et trouverons une solution appropriée.',
        },
      ],
    },
    account: {
      title: 'Compte',
      icon: '👤',
      questions: [
        {
          q: 'Comment créer un compte ?',
          a: 'Utilisez la page Inscription : vous créez un compte Firebase (email et mot de passe), puis le serveur enregistre votre profil Maxfly et un document dans Firestore (collection users). Les comptes créés sont des comptes client.',
        },
        {
          q: 'J\'ai oublié mon mot de passe',
          a: 'Sur la page Connexion, utilisez « Mot de passe oublié » : Firebase envoie un lien sécurisé à votre email. Une fois connecté, vous pouvez aussi changer le mot de passe depuis Mon profil → Mot de passe.',
        },
        {
          q: 'Comment modifier mes informations ?',
          a: 'Allez dans "Mon Profil" depuis le menu. Vous pouvez modifier vos informations personnelles, votre adresse de livraison et votre mot de passe.',
        },
        {
          q: 'Puis-je avoir plusieurs comptes ?',
          a: 'Chaque compte doit être associé à une adresse email unique. Un compte par personne est recommandé pour faciliter le suivi de vos commandes.',
        },
      ],
    },
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div>
      <Navbar />

      <div className="container">
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '40px 20px' }}>
          <h1 style={{ 
            fontSize: '36px', 
            fontWeight: 'bold', 
            color: '#FF0000', 
            marginBottom: '10px',
            textAlign: 'center'
          }}>
            🆘 Centre d'aide Maxfly
          </h1>
          <p style={{ 
            fontSize: '18px', 
            color: '#666', 
            textAlign: 'center',
            marginBottom: '40px'
          }}>
            Trouvez rapidement les réponses à vos questions
          </p>

          {/* Navigation des sections */}
          <div style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '40px',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
            <button
              onClick={() => setActiveSection('faq')}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: '8px',
                background: activeSection === 'faq' ? '#FF0000' : '#f0f0f0',
                color: activeSection === 'faq' ? 'white' : '#333',
                cursor: 'pointer',
                fontWeight: activeSection === 'faq' ? 'bold' : 'normal',
                fontSize: '16px',
              }}
            >
              ❓ FAQ
            </button>
            <button
              onClick={() => setActiveSection('guide')}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: '8px',
                background: activeSection === 'guide' ? '#FF0000' : '#f0f0f0',
                color: activeSection === 'guide' ? 'white' : '#333',
                cursor: 'pointer',
                fontWeight: activeSection === 'guide' ? 'bold' : 'normal',
                fontSize: '16px',
              }}
            >
              📖 Guide d'utilisation
            </button>
            <button
              onClick={() => setActiveSection('contact')}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: '8px',
                background: activeSection === 'contact' ? '#FF0000' : '#f0f0f0',
                color: activeSection === 'contact' ? 'white' : '#333',
                cursor: 'pointer',
                fontWeight: activeSection === 'contact' ? 'bold' : 'normal',
                fontSize: '16px',
              }}
            >
              📧 Nous contacter
            </button>
          </div>

          {/* Section FAQ */}
          {activeSection === 'faq' && (
            <div>
              <h2 style={{ fontSize: '28px', marginBottom: '30px', color: '#333' }}>
                Questions fréquemment posées
              </h2>
              
              {Object.entries(faqCategories).map(([key, category]) => (
                <div key={key} style={{ marginBottom: '40px' }}>
                  <h3 style={{
                    fontSize: '22px',
                    marginBottom: '20px',
                    color: '#FF0000',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  }}>
                    <span style={{ fontSize: '28px' }}>{category.icon}</span>
                    {category.title}
                  </h3>
                  
                  <div style={{ display: 'grid', gap: '15px' }}>
                    {category.questions.map((item, index) => {
                      const faqKey = `${key}-${index}`;
                      const isOpen = openFaq === faqKey;
                      
                      return (
                        <div
                          key={faqKey}
                          className="card"
                          style={{
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            overflow: 'hidden',
                          }}
                        >
                          <button
                            onClick={() => toggleFaq(faqKey)}
                            style={{
                              width: '100%',
                              padding: '20px',
                              background: 'none',
                              border: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#333',
                            }}
                          >
                            <span>{item.q}</span>
                            <span style={{ fontSize: '20px', color: '#FF0000' }}>
                              {isOpen ? '−' : '+'}
                            </span>
                          </button>
                          
                          {isOpen && (
                            <div style={{
                              padding: '0 20px 20px 20px',
                              color: '#666',
                              lineHeight: '1.6',
                              whiteSpace: 'pre-line',
                            }}>
                              {item.a}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Section Guide d'utilisation */}
          {activeSection === 'guide' && (
            <div>
              <h2 style={{ fontSize: '28px', marginBottom: '30px', color: '#333' }}>
                Guide d'utilisation
              </h2>

              <div style={{ display: 'grid', gap: '30px' }}>
                {/* Étape 1 */}
                <div className="card">
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      background: '#FF0000',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      fontWeight: 'bold',
                      flexShrink: 0,
                    }}>
                      1
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#333' }}>
                        Créer votre compte
                      </h3>
                      <p style={{ color: '#666', lineHeight: '1.6' }}>
                        Inscrivez-vous sur Maxfly en fournissant vos informations personnelles (nom, prénom, email, mot de passe). 
                        Une fois inscrit, vous pouvez accéder à toutes les fonctionnalités de la plateforme.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Étape 2 */}
                <div className="card">
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      background: '#FF0000',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      fontWeight: 'bold',
                      flexShrink: 0,
                    }}>
                      2
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#333' }}>
                        Trouver un produit
                      </h3>
                      <p style={{ color: '#666', lineHeight: '1.6' }}>
                        Parcourez les plateformes internationales (Shein, Amazon, Alibaba, etc.) et trouvez le produit de votre choix. 
                        Copiez le lien du produit ou prenez une capture d'écran.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Étape 3 */}
                <div className="card">
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      background: '#FF0000',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      fontWeight: 'bold',
                      flexShrink: 0,
                    }}>
                      3
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#333' }}>
                        Créer une commande
                      </h3>
                      <p style={{ color: '#666', lineHeight: '1.6' }}>
                        Allez dans "Nouvelle Commande", collez le lien du produit ou téléchargez la capture d'écran. 
                        Sélectionnez la plateforme, ajoutez les détails (taille, couleur, quantité) et soumettez votre commande.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Étape 4 */}
                <div className="card">
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      background: '#FF0000',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      fontWeight: 'bold',
                      flexShrink: 0,
                    }}>
                      4
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#333' }}>
                        Attendre la validation
                      </h3>
                      <p style={{ color: '#666', lineHeight: '1.6' }}>
                        Un agent Maxfly examine votre commande et définit le prix total (produit + commission + livraison). 
                        Vous recevrez une notification dès que le prix est disponible.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Étape 5 */}
                <div className="card">
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      background: '#FF0000',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      fontWeight: 'bold',
                      flexShrink: 0,
                    }}>
                      5
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#333' }}>
                        Payer votre commande
                      </h3>
                      <p style={{ color: '#666', lineHeight: '1.6' }}>
                        Une fois le prix défini, allez dans "Mes Commandes" et cliquez sur "Payer maintenant". 
                        Choisissez votre méthode de paiement (Stripe ou Mobile Money) et finalisez le paiement.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Étape 6 */}
                <div className="card">
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      background: '#FF0000',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      fontWeight: 'bold',
                      flexShrink: 0,
                    }}>
                      6
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#333' }}>
                        Suivre votre commande
                      </h3>
                      <p style={{ color: '#666', lineHeight: '1.6' }}>
                        Après le paiement, votre commande sera expédiée. Vous recevrez un Tracking ID et pourrez suivre 
                        chaque étape de la livraison depuis votre dashboard jusqu'à la réception.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section Contact */}
          {activeSection === 'contact' && (
            <div>
              <h2 style={{ fontSize: '28px', marginBottom: '30px', color: '#333' }}>
                Nous contacter
              </h2>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '40px' }}>
                <div className="card" style={{ textAlign: 'center', padding: '30px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '15px' }}>📧</div>
                  <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#333' }}>Email</h3>
                  <p style={{ color: '#666', marginBottom: '10px' }}>
                    <a href={`mailto:${config.CONTACT_EMAIL}`} style={{ color: 'inherit' }}>
                      {config.CONTACT_EMAIL}
                    </a>
                  </p>
                  <p style={{ color: '#666', fontSize: '14px' }}>Réponse sous 24h</p>
                </div>

                <div className="card" style={{ textAlign: 'center', padding: '30px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '15px' }}>📱</div>
                  <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#333' }}>Téléphone</h3>
                  <div style={{ color: '#666', marginBottom: '10px', lineHeight: 1.8 }}>
                    {config.CONTACT_PHONES.map(({ tel, display }) => (
                      <div key={tel}>
                        <a href={`tel:${tel}`} style={{ color: 'inherit' }}>
                          {display}
                        </a>
                      </div>
                    ))}
                  </div>
                  <p style={{ color: '#666', fontSize: '14px' }}>Lun-Ven: 9h-18h</p>
                </div>

                <div className="card" style={{ textAlign: 'center', padding: '30px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '15px' }}>💬</div>
                  <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#333' }}>Support / Chat</h3>
                  <p style={{ color: '#666', marginBottom: '15px', fontSize: '14px' }}>
                    Connectez-vous puis ouvrez la page Support pour dialoguer avec un agent (chat intégré).
                  </p>
                  <Link to="/support" className="btn btn-primary" style={{ display: 'inline-block' }}>
                    Accéder au support
                  </Link>
                </div>
              </div>

              <div className="card" style={{ padding: '30px' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '20px', color: '#333' }}>
                  Formulaire de contact
                </h3>
                <form onSubmit={handleContactSubmit}>
                  <div className="form-group">
                    <label htmlFor="contactName">Nom complet *</label>
                    <input
                      type="text"
                      id="contactName"
                      name="name"
                      required
                      placeholder="Votre nom"
                      value={contact.name}
                      onChange={handleContactChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="contactEmail">Email *</label>
                    <input
                      type="email"
                      id="contactEmail"
                      name="email"
                      required
                      placeholder="votre@email.com"
                      value={contact.email}
                      onChange={handleContactChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="contactSubject">Sujet *</label>
                    <select
                      id="contactSubject"
                      name="subject"
                      required
                      value={contact.subject}
                      onChange={handleContactChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '16px',
                      }}
                    >
                      <option value="">Sélectionnez un sujet</option>
                      <option value="order">Question sur une commande</option>
                      <option value="payment">Problème de paiement</option>
                      <option value="delivery">Question sur la livraison</option>
                      <option value="account">Problème de compte</option>
                      <option value="other">Autre</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="contactMessage">Message *</label>
                    <textarea
                      id="contactMessage"
                      name="message"
                      required
                      rows="6"
                      placeholder="Décrivez votre question ou problème..."
                      value={contact.message}
                      onChange={handleContactChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                      }}
                    />
                  </div>
                  <p style={{ color: '#666', fontSize: '13px', marginBottom: '15px' }}>
                    En validant, votre application mail s’ouvre avec un message prérempli vers{' '}
                    <strong>{config.CONTACT_EMAIL}</strong>.
                  </p>
                  <button type="submit" className="btn btn-primary">
                    Ouvrir ma messagerie
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Help;

